import { useCallback, useRef, useState } from 'react';

/**
 * Tracks whether ANY upload control on a form is still transferring, so the
 * form can disable Save until they finish.
 *
 * Why a form needs this. Each upload control runs the presign->PUT flow
 * internally, so the surrounding form cannot otherwise tell that one is in
 * flight. Saving mid-transfer then drops the image SILENTLY: the control has
 * not reported its fileKey yet, the payload omits the key, and the save
 * succeeds against the previously stored file with no error anywhere. Reported
 * in manual testing as "it saved but the new logo never appeared" - the user
 * had simply clicked Save before the transfer finished.
 *
 * Counts per control rather than holding one boolean, because a form can carry
 * several (the partner profile has a logo and a cover photo). With one flag the
 * first upload to finish would clear it while the second was still running.
 *
 * `track(id)` returns the SAME callback for a given id on every render, so the
 * child's effect fires on real state changes rather than on each re-render.
 */
export function useUploadsInFlight(): {
  isUploading: boolean;
  track: (id: string) => (isUploading: boolean) => void;
} {
  const [active, setActive] = useState<ReadonlySet<string>>(() => new Set());
  const handlers = useRef(new Map<string, (isUploading: boolean) => void>());

  const track = useCallback((id: string) => {
    const existing = handlers.current.get(id);
    if (existing) return existing;

    const handler = (isUploading: boolean): void => {
      setActive((prev) => {
        if (prev.has(id) === isUploading) return prev;
        const next = new Set(prev);
        if (isUploading) next.add(id);
        else next.delete(id);
        return next;
      });
    };
    handlers.current.set(id, handler);
    return handler;
  }, []);

  return { isUploading: active.size > 0, track };
}
